import { computed, Injectable, signal } from '@angular/core';

/**
 * FlowStep represents a step in the application flow the user can reach.
 * These currently hold all possible steps in the modal flow for strict typing
 */
export type FlowStep =
  | 'login'
  | 'verify-otp'
  | 'application'
  | 'application-verify-otp'
  | 'application-denial'
  | 'application-decision'
  | 'application-decision-warn'
  | 'view-transaction'
  | 'cancel'
  | 'warning';

/** FlowPath represents a path a user can take in the system */
export type FlowPath = 'login-flow' | 'application-flow';

/** Action the layout Go back button should perform for a given step */
export type GoBackAction = { type: 'navigate'; step: FlowStep } | { type: 'close' };

const FLOW_STEPS: readonly FlowStep[] = [
  'login',
  'verify-otp',
  'application',
  'application-verify-otp',
  'application-denial',
  'application-decision',
  'application-decision-warn',
  'view-transaction',
  'cancel',
  'warning',
];

/**
 * FlowState holds the current state of the user moving through the system
 */
interface FlowState {
  completedSteps: Set<FlowStep>;
  visitedSteps: Set<FlowStep>;
  currentStep: FlowStep | null;
  activePath: FlowPath | null;
}

/** Parse the active flow step from a router URL */
export function flowStepFromUrl(url: string): FlowStep | null {
  const segment = url.split('?')[0].split('/').filter(Boolean).pop();
  if (!segment) {
    return null;
  }

  return FLOW_STEPS.includes(segment as FlowStep) ? (segment as FlowStep) : null;
}

@Injectable({
  providedIn: 'root',
})
export class FlowStateService {
  // Record the current flow state
  private flowState = signal<FlowState>({
    completedSteps: new Set(),
    visitedSteps: new Set(),
    currentStep: null,
    activePath: null,
  });

  // Export completed steps and the current step
  readonly completedSteps = computed(() => this.flowState().completedSteps);
  readonly currentStep = computed(() => this.flowState().currentStep);
  readonly activePath = computed(() => this.flowState().activePath);
  readonly showGoBack = computed(() => {
    const currentStep = this.currentStep();
    return ['cancel', 'view-transaction', 'application-decision', 'application-denial', 'warning', 'application-decision-warn'].some(
      (s) => s === currentStep,
    );
  });

  /**
   * Mark a step as completed and set it as current
   * @param step The step to complete
   * @param path The path this step is located on
   */
  completeStep(step: FlowStep, path?: FlowPath): void {
    this.flowState.update((state) => ({
      ...state,
      completedSteps: new Set([...state.completedSteps, step]),
      visitedSteps: new Set([...state.visitedSteps, step]),
      currentStep: step,
      activePath: path || state.activePath,
    }));
  }

  /**
   * Check if a step has been completed
   * @param step The step to check
   * @return true if completed, false if not
   */
  hasCompletedStep(step: FlowStep): boolean {
    return this.flowState().completedSteps.has(step);
  }

  /**
   * Record that the user has reached a step (distinct from completing it)
   */
  visitStep(step: FlowStep): void {
    this.flowState.update((state) => ({
      ...state,
      visitedSteps: new Set([...state.visitedSteps, step]),
      currentStep: step,
    }));
  }

  hasVisitedStep(step: FlowStep): boolean {
    return this.flowState().visitedSteps.has(step);
  }

  /**
   * Resolve where the layout Go back button should navigate from the current step
   */
  getGoBackAction(currentStep: FlowStep): GoBackAction {
    switch (currentStep) {
      case 'login':
        return { type: 'close' };
      case 'application':
        return this.hasVisitedStep('login') ? { type: 'navigate', step: 'login' } : { type: 'close' };
      case 'verify-otp':
        return { type: 'navigate', step: 'login' };
      case 'application-verify-otp':
        return { type: 'navigate', step: 'application' };
      case 'application-decision-warn':
        return this.hasCompletedStep('application-verify-otp')
          ? { type: 'navigate', step: 'application-verify-otp' }
          : { type: 'navigate', step: 'application' };
      case 'warning':
        return { type: 'close' };
      default:
        return { type: 'close' };
    }
  }

  /**
   * Reset the flow state
   */
  reset(): void {
    this.flowState.set({
      completedSteps: new Set(),
      visitedSteps: new Set(),
      currentStep: null,
      activePath: null,
    });
  }
}
